import { useState } from "react";
import type React from "react";
import {
  Button,
  Descriptions,
  Drawer,
  Empty,
  Spin,
  Tag,
  Typography,
} from "antd";
import { useQuery } from "@tanstack/react-query";
import cdrReviewApi from "@/services/cdrReviewApi";
import ClinicalReviewDispositionModal from "./ClinicalReviewDispositionModal";
import ClinicalReviewFlags from "./ClinicalReviewFlags";
import { CDR_STATUS_COLOR, CDR_STATUS_LABEL, fieldLabel } from "./fieldLabel";

type Props = {
  trialId: string;
  itemId: number | null;
  onClose: () => void;
  onDispositioned: () => void;
};

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export default function ClinicalReviewDrawer({
  trialId,
  itemId,
  onClose,
  onDispositioned,
}: Props) {
  const [dispositioning, setDispositioning] = useState(false);

  const itemQuery = useQuery({
    queryKey: ["cdr-review-item", trialId, itemId],
    queryFn: () => cdrReviewApi.item(trialId, itemId as number),
    enabled: itemId !== null,
  });

  const detail = itemQuery.data;
  const item = detail?.item ?? null;

  return (
    <>
      <Drawer
        title={item ? item.display_id : "Review record"}
        open={itemId !== null}
        onClose={onClose}
        width={520}
        destroyOnHidden
      >
        {itemQuery.isLoading && (
          <div className="flex h-40 items-center justify-center">
            <Spin />
          </div>
        )}
        {!itemQuery.isLoading && (!detail || !item) && (
          <Empty description="Could not load this record." />
        )}
        {item && detail && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <Tag>{item.domain_label}</Tag>
              <Tag color={CDR_STATUS_COLOR[item.status]}>
                {CDR_STATUS_LABEL[item.status] ?? item.status}
              </Tag>
              {item.linked_issue && (
                <Tag color="error">
                  Open discrepancy: {item.linked_issue.issue_id}
                </Tag>
              )}
            </div>

            <Typography.Text type="secondary" className="text-xs">
              {item.summary}
            </Typography.Text>

            <ClinicalReviewFlags flags={detail.flags ?? []} />

            <Descriptions
              size="small"
              column={1}
              bordered
              items={[
                {
                  key: "subject",
                  label: "Subject",
                  children: item.subject_id ?? "—",
                },
                { key: "site", label: "Site", children: item.site_id ?? "—" },
              ]}
            />

            <section className="flex flex-col gap-1">
              <SectionTitle>Record</SectionTitle>
              {detail.record.found ? (
                <Descriptions
                  size="small"
                  column={1}
                  bordered
                  items={detail.record.fields.map((field) => ({
                    key: field.name,
                    label: fieldLabel(field.name),
                    children: renderValue(field.value),
                  }))}
                />
              ) : (
                <Empty description="The underlying clinical record could not be found." />
              )}
            </section>

            {detail.marks.length > 0 && (
              <section className="flex flex-col gap-1">
                <SectionTitle>Review history</SectionTitle>
                <div className="flex flex-col gap-2">
                  {detail.marks.map((mark) => (
                    <div
                      key={mark.id}
                      className="rounded-md border border-[#E6ECF1] px-3 py-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#2B4752]">
                          {CDR_STATUS_LABEL[mark.action] ?? mark.action}
                        </span>
                        <span className="text-[#8FA5A0]">
                          {new Date(mark.at).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-[#556C75]">
                        {mark.by || "Unknown reviewer"}
                      </div>
                      {mark.comment && (
                        <div className="mt-1 text-[#2B4752]">
                          {mark.comment}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            <Button type="primary" onClick={() => setDispositioning(true)}>
              Record disposition
            </Button>
          </div>
        )}
      </Drawer>

      <ClinicalReviewDispositionModal
        trialId={trialId}
        item={dispositioning ? item : null}
        onClose={() => setDispositioning(false)}
        onDispositioned={() => {
          itemQuery.refetch();
          onDispositioned();
        }}
      />
    </>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="m-0 text-xs font-semibold tracking-wider text-[#556C75] uppercase">
      {children}
    </h3>
  );
}
